import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./RailwayScales.css";
import { useProducts, type RailwayScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const RailwayScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<RailwayScalesSpecs>('railway-scales', editMode);
  const navigate = useNavigate();

  const optionalAddons = [
    "Displays remotos",
    "Herramientas de calibración",
    "Software de gestión de datos",
    "Sistemas de comunicación remota",
    "Impresoras adicionales",
    "Sistemas de backup",
  ];

  const applications = [
    {
      title: "Industria Minera",
      description: "Pesaje de vagones cargados con minerales y materiales de construcción.",
    },
    {
      title: "Sector Agrícola",
      description: "Control de peso de granos, fertilizantes y productos agrícolas.",
    },
    {
      title: "Industria Química",
      description: "Pesaje de materias primas químicas y productos procesados.",
    },
    {
      title: "Logística",
      description: "Control de carga para optimización de transporte ferroviario.",
    },
    {
      title: "Siderurgia",
      description: "Pesaje de materiales siderúrgicos y productos metalúrgicos.",
    },
    {
      title: "Energía",
      description: "Control de carbón y otros combustibles para plantas de energía.",
    },
  ];

  const advantages = [
    {
      title: "Experiencia Comprobada",
      description: "Amplia trayectoria en implementación de proyectos ferroviarios.",
    },
    {
      title: "Mínimas Obras Civiles",
      description: "Instalación con mínima interrupción de operaciones existentes.",
    },
    {
      title: "Flexibilidad",
      description: "Dos sistemas diferentes para adaptarse a cualquier necesidad.",
    },
    {
      title: "Alta Capacidad",
      description: "Manejo de cargas desde 80,000 kg hasta 200,000 kg.",
    },
    {
      title: "Resistencia Climática",
      description: "Operación confiable en condiciones extremas de temperatura.",
    },
    {
      title: "Sistema Completo",
      description: "Incluye todos los componentes necesarios para la operación.",
    },
  ];

  return (
    <div className="rws-page">
      {error && <div className="rws-status-bar rws-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="rws-hero">
        <div className="container rws-hero-content">
          <nav className="rws-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="rws-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="rws-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas para Ferrocarril</span>
          </nav>
          <h1 className="rws-hero-title">Básculas para Ferrocarril</h1>
          <p className="rws-hero-subtitle">
            Amplia experiencia en comercialización e implementación de proyectos
            de básculas ferroviarias
          </p>
          {authType === 'internal' && (
            <button
              className={`rws-btn-edit-mode${editMode ? ' rws-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="rws-hero-image" aria-hidden="true">
          <img src="/images/railway-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="rws-overview">
        <div className="container">
          <div className="rws-overview-grid">
            <div className="rws-overview-text">
              <span className="rws-label">Descripción general</span>
              <h2>Soluciones ferroviarias de alto rendimiento</h2>
              <p>
                En Automatización Básculas y Control, tenemos amplia experiencia
                en la comercialización e implementación de proyectos de básculas
                para ferrocarril. Ofrecemos dos sistemas principales que se
                adaptan a diferentes necesidades operativas y de
                infraestructura.
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="rws-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Sistemas de Báscula Ferroviaria</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/railway-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.type ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.capacities?.length ? [{ icon: '📦', label: 'Capacidades', items: (model.specs?.capacities  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.weighingModes?.length ? [{ icon: '⚖️', label: 'Modos', items: (model.specs?.weighingModes  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.platformOptions?.length ? [{ icon: '📐', label: 'Plataformas', items: (model.specs?.platformOptions  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.technicalSpecs?.length ? [{ icon: '🔧', label: 'Especificaciones', items: (model.specs?.technicalSpecs  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.included?.length ? [{ icon: '📋', label: 'Incluye', items: (model.specs?.included  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Optional add-ons ─────────────────────────────────── */}
      <section className="rws-addons">
        <div className="container">
          <div className="rws-addons-inner">
            <div className="rws-addons-header">
              <span className="rws-label">Personalización</span>
              <h2 className="rws-section-title">Complementos opcionales</h2>
              <p>
                Adapte su sistema ferroviario a los requerimientos específicos
                de su operación logística.
              </p>
            </div>
            <ul className="rws-addon-list" aria-label="Complementos opcionales">
              {optionalAddons.map((addon, index) => (
                <li key={index} className="rws-addon-item">
                  <svg className="rws-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {addon}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="rws-applications">
        <div className="container">
          <span className="rws-label rws-label--light">Industrias</span>
          <h2 className="rws-section-title rws-section-title--light">Aplicaciones</h2>
          <div className="rws-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="rws-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="rws-advantages">
        <div className="container">
          <span className="rws-label">Ventajas</span>
          <h2 className="rws-section-title">Por qué elegir nuestras básculas ferroviarias</h2>
          <div className="rws-advantages-grid">
            {advantages.map((item, index) => (
              <div key={index} className="rws-advantage-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RailwayScales;
